/**
 * useRegulatoryDeadlines — DRR-309 Phase 9
 *
 * Derives regulatory deadline data from journey tasks.
 * Tasks with mandateRef and deadline become deadline entries.
 * Status computed from time remaining vs total duration.
 */

import { useMemo } from 'react';
import type { RegulatoryDeadline } from '../overlays/types';

interface Task {
  id: string;
  title: string;
  status: string;
  mandateRef?: string | null;
  deadline?: string | null;
  createdAt?: string | null;
}

interface UseRegulatoryDeadlinesOptions {
  /** Task list from journey view */
  tasks: Task[];
  /** Instance started at (fallback for task createdAt) */
  journeyStartedAt?: Date;
}

/**
 * Computes deadline status based on time remaining.
 * - completed: task status is COMPLETED
 * - breached: past deadline
 * - at_risk: < 25% time remaining
 * - on_track: >= 25% time remaining
 */
function computeDeadlineStatus(
  taskStatus: string,
  startedAt: Date,
  deadlineAt: Date
): RegulatoryDeadline['status'] {
  if (taskStatus === 'COMPLETED') return 'completed';

  const now = Date.now();
  const deadline = deadlineAt.getTime();

  if (now >= deadline) return 'breached';

  const start = startedAt.getTime();
  const total = deadline - start;
  const remaining = deadline - now;
  const percentRemaining = Math.max(0, Math.round((remaining / total) * 100));

  if (percentRemaining < 25) return 'at_risk';
  return 'on_track';
}

/**
 * Computes percent of time remaining until deadline.
 */
function computePercentRemaining(startedAt: Date, deadlineAt: Date): number {
  const now = Date.now();
  const deadline = deadlineAt.getTime();
  const start = startedAt.getTime();

  if (now >= deadline) return 0;

  const total = deadline - start;
  const remaining = deadline - now;

  return Math.max(0, Math.min(100, Math.round((remaining / total) * 100)));
}

/**
 * Hook to compute regulatory deadlines from journey tasks.
 * Only tasks with both mandateRef (regulatory citation) AND deadline are included.
 */
export function useRegulatoryDeadlines({
  tasks,
  journeyStartedAt,
}: UseRegulatoryDeadlinesOptions): RegulatoryDeadline[] {
  return useMemo(() => {
    const regulatoryTasks = tasks.filter(
      (t) => t.mandateRef && t.deadline
    );

    return regulatoryTasks.map((task) => {
      const deadlineAt = new Date(task.deadline!);
      const startedAt = task.createdAt
        ? new Date(task.createdAt)
        : journeyStartedAt ?? new Date(deadlineAt.getTime() - 72 * 60 * 60 * 1000); // Default: assume 72hr window

      const status = computeDeadlineStatus(task.status, startedAt, deadlineAt);
      const percentRemaining = computePercentRemaining(startedAt, deadlineAt);

      return {
        id: task.id,
        label: task.title,
        citation: task.mandateRef!,
        deadlineAt,
        startedAt,
        percentRemaining,
        status,
      };
    }).sort((a, b) => a.deadlineAt.getTime() - b.deadlineAt.getTime());
  }, [tasks, journeyStartedAt]);
}

export default useRegulatoryDeadlines;
